// SCR-STAFF-1200 — همه تنظیمات (US-CFG-003، 004): رجیستری تنظیمات همه ماژول‌ها گروه‌بندی‌شده با جستجو، مقدار مؤثر و منبع آن
// (پیش‌فرض، مستأجر، شعبه)، ویرایشگر متناسب با نوع (string، bool، int، long) و فقط‌خواندنی با «نیازمند ارتقا» برای Feature
// غیرفعال (AC-CFG-003.1)؛ تاریخچه هر تنظیم با کاربر، زمان، دامنه و مقدار قبل و بعد و «بازگرداندن» که تغییر تازه ثبت می‌کند
// (AC-CFG-004.1).
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { unwrap, type SettingView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ErrorState,
  faDigits,
  JalaliDate,
  ListPage,
  LoadingState,
  latinDigits,
  UpgradeBadge,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';
import { SUBSCRIPTION_PATH } from '../../navigation';
import { cfgKeys } from './common';

export const SCR_STAFF_1200 = 'SCR-STAFF-1200';

/** نمایش مقدار JSON تنظیم. */
export function formatSettingValue(value: unknown, t: (key: string) => string): string {
  if (value === null || value === undefined) {
    return t('common:states.none');
  }
  if (typeof value === 'boolean') {
    return value ? t('cfg:settings.on') : t('cfg:settings.off');
  }
  if (typeof value === 'number') {
    return faDigits(String(value));
  }
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function parseValue(setting: SettingView, raw: string | boolean): unknown {
  if (setting.valueType === 'bool') {
    return Boolean(raw);
  }
  if (setting.valueType === 'int' || setting.valueType === 'long') {
    const text = latinDigits(String(raw)).trim();
    return /^-?\d+$/.test(text) ? Number(text) : Number.NaN;
  }
  return String(raw);
}

function EditDialog({ setting, onClose }: { setting: SettingView; onClose: () => void }) {
  const { t } = useTranslation('cfg');
  const { tenantApi } = useTenantSession();
  const queryClient = useQueryClient();
  const titleId = useId();
  const [raw, setRaw] = useState<string | boolean>(
    setting.valueType === 'bool' ? Boolean(setting.value) : String(setting.value ?? ''),
  );
  const parsed = parseValue(setting, raw);
  const invalid = typeof parsed === 'number' && Number.isNaN(parsed);

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/configuration/settings/{key}', {
          params: { path: { key: setting.key } },
          body: { scope: 'Tenant', scopeId: null, value: parsed as SettingView['value'] },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['configuration', 'settings'] });
      onClose();
    },
  });

  const label = t(setting.titleKey, { defaultValue: setting.key });
  return (
    <Dialog
      open
      onClose={save.isPending ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id={titleId}>{t('cfg:settings.edit_title', { name: label })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {setting.valueType === 'bool' ? (
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(raw)}
                  onChange={(event) => setRaw(event.target.checked)}
                  data-testid="setting-bool"
                />
              }
              label={label}
            />
          ) : (
            <TextField
              label={label}
              value={String(raw)}
              onChange={(event) => setRaw(event.target.value)}
              fullWidth
              error={invalid}
              helperText={
                invalid
                  ? t('cfg:settings.number_invalid')
                  : t('cfg:settings.default_value', { value: formatSettingValue(setting.defaultValue, t) })
              }
              slotProps={{
                htmlInput: {
                  dir: 'ltr',
                  inputMode: setting.valueType === 'string' ? undefined : 'numeric',
                  'data-testid': 'setting-value',
                },
              }}
            />
          )}
          {save.error ? <ErrorState error={save.error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          disabled={invalid || save.isPending}
          data-testid="setting-save"
        >
          {t('common:actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function HistoryDialog({
  setting,
  canEdit,
  onClose,
}: {
  setting: SettingView;
  canEdit: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('cfg');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const titleId = useId();

  const history = useQuery({
    queryKey: cfgKeys.history(session?.tenantId, setting.key),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/configuration/settings/{key}/history', {
          params: { path: { key: setting.key }, query: { limit: 50 } },
        }),
      ),
  });
  const revert = useMutation({
    mutationFn: (entryId: string) =>
      unwrap(
        tenantApi.POST('/api/v1/configuration/settings/{key}/history/{entryId}:revert', {
          params: { path: { key: setting.key, entryId } },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['configuration', 'settings'] });
      await history.refetch();
    },
  });

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="md" fullWidth>
      <DialogTitle id={titleId}>
        {t('cfg:settings.history_title', { name: t(setting.titleKey, { defaultValue: setting.key }) })}
      </DialogTitle>
      <DialogContent>
        {history.isPending ? (
          <LoadingState rows={2} />
        ) : history.error ? (
          <ErrorState error={history.error} compact />
        ) : (history.data?.items.length ?? 0) === 0 ? (
          <Typography color="text.secondary">{t('cfg:settings.history_empty')}</Typography>
        ) : (
          <Table size="small" aria-label={t('cfg:settings.history_caption')} data-testid="setting-history">
            <TableHead>
              <TableRow>
                <TableCell>{t('cfg:settings.history_at')}</TableCell>
                <TableCell>{t('cfg:settings.scope')}</TableCell>
                <TableCell>{t('cfg:settings.history_old')}</TableCell>
                <TableCell>{t('cfg:settings.history_new')}</TableCell>
                <TableCell>{t('cfg:settings.history_actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.data?.items.map((entry) => (
                <TableRow key={entry.id} data-testid={`history-${entry.id}`}>
                  <TableCell>
                    <JalaliDate value={entry.changedAt} withTime />
                  </TableCell>
                  <TableCell>{t(`cfg:source.${entry.scope}`, { defaultValue: entry.scope })}</TableCell>
                  <TableCell>{formatSettingValue(entry.oldValue, t)}</TableCell>
                  <TableCell>{formatSettingValue(entry.newValue, t)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => revert.mutate(entry.id)}
                      disabled={!canEdit || revert.isPending || entry.oldValue === null}
                      data-testid={`history-revert-${entry.id}`}
                    >
                      {t('cfg:settings.revert')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {revert.error ? <ErrorState error={revert.error} compact /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function AllSettingsPage() {
  const { t } = useTranslation('cfg');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<SettingView | null>(null);
  const [history, setHistory] = useState<SettingView | null>(null);

  const settings = useQuery({
    queryKey: cfgKeys.all(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/configuration/settings')),
  });

  const groups = useMemo(() => {
    const needle = latinDigits(search).trim().toLowerCase();
    const items = (settings.data?.items ?? []).filter((item) => {
      if (!needle) {
        return true;
      }
      const title = t(item.titleKey, { defaultValue: item.key }).toLowerCase();
      return (
        item.key.toLowerCase().includes(needle) ||
        title.includes(needle) ||
        item.group.toLowerCase().includes(needle)
      );
    });
    const byGroup = new Map<string, SettingView[]>();
    for (const item of items) {
      byGroup.set(item.group, [...(byGroup.get(item.group) ?? []), item]);
    }
    return [...byGroup.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [settings.data, search, t]);

  const featureOff = (item: SettingView) =>
    Boolean(item.featureKey) && !access.featureEnabled(item.featureKey!);
  const editable = (item: SettingView) =>
    item.canEdit && !item.readOnly && !featureOff(item) && access.accessMode === 'Full';

  return (
    <ListPage
      screenId={SCR_STAFF_1200}
      titleKey="cfg:settings.title"
      subtitleKey="cfg:settings.subtitle"
      filters={
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('cfg:settings.search')}
          size="small"
          fullWidth
          slotProps={{
            htmlInput: { 'aria-label': t('cfg:settings.search'), 'data-testid': 'settings-search' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      }
    >
      {settings.isPending ? (
        <LoadingState rows={4} />
      ) : settings.error ? (
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      ) : groups.length === 0 ? (
        <Typography color="text.secondary" data-testid="settings-empty">
          {t('cfg:settings.empty')}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {groups.map(([group, items]) => (
            <Paper key={group} variant="outlined" data-testid={`settings-group-${group}`}>
              <Typography variant="subtitle1" sx={{ px: 2, pt: 1.5 }}>
                {t(`cfg:groups.${group.replace(/\./g, '_')}`, { defaultValue: group })}
              </Typography>
              <Table
                size="small"
                aria-label={t(`cfg:groups.${group.replace(/\./g, '_')}`, { defaultValue: group })}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{t('cfg:settings.name')}</TableCell>
                    <TableCell>{t('cfg:settings.value')}</TableCell>
                    <TableCell>{t('cfg:settings.source')}</TableCell>
                    <TableCell>{t('cfg:settings.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.key} data-testid={`setting-${item.key}`}>
                      <TableCell>
                        <Typography variant="body2">
                          {t(item.titleKey, { defaultValue: item.key })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="bdi" dir="ltr">
                          {item.key}
                        </Typography>
                      </TableCell>
                      <TableCell data-testid={`setting-value-${item.key}`}>
                        {formatSettingValue(item.value, t)}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t(`cfg:source.${item.source}`, { defaultValue: item.source })}
                          />
                          {featureOff(item) && item.featureKey && (
                            <UpgradeBadge feature={item.featureKey} to={SUBSCRIPTION_PATH} />
                          )}
                          {item.readOnly && !featureOff(item) && (
                            <Chip size="small" label={t('cfg:settings.read_only')} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t('cfg:settings.edit')}>
                          <span>
                            <IconButton
                              aria-label={t('cfg:settings.edit')}
                              onClick={() => setEditing(item)}
                              disabled={!editable(item)}
                              data-testid={`setting-edit-${item.key}`}
                            >
                              <EditOutlinedIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('cfg:settings.history')}>
                          <IconButton
                            aria-label={t('cfg:settings.history')}
                            onClick={() => setHistory(item)}
                            data-testid={`setting-history-${item.key}`}
                          >
                            <HistoryOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ))}
        </Stack>
      )}
      {editing && <EditDialog setting={editing} onClose={() => setEditing(null)} />}
      {history && (
        <HistoryDialog setting={history} canEdit={editable(history)} onClose={() => setHistory(null)} />
      )}
    </ListPage>
  );
}
